import React, { useState } from 'react';
import { Volume2, VolumeX, Menu, X, Sparkles, Guitar, PlayCircle, Heart } from 'lucide-react';
import { YoutubeIcon } from './icons/YoutubeIcon';
import { soundEngine } from '../utils/audio';

interface NavbarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({ activeTab, setActiveTab }) => {
  const [isMuted, setIsMuted] = useState(soundEngine.isMuted);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleSound = () => {
    soundEngine.isMuted = !soundEngine.isMuted;
    setIsMuted(soundEngine.isMuted);
    if (!soundEngine.isMuted) {
      soundEngine.playSparkleSfx();
    }
  };

  const navItems = [
    { id: 'home', label: 'Home', icon: Sparkles },
    { id: 'videos', label: 'Videos', icon: PlayCircle },
    { id: 'guitar', label: 'Guitar Academy', icon: Guitar },
  ];

  const handleNavClick = (id: string) => {
    soundEngine.playPopSfx();
    setActiveTab(id);
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b-2 border-pink-100 shadow-sm transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo brand */}
          <button 
            onClick={() => handleNavClick('home')}
            className="flex items-center gap-3 group text-left cursor-pointer transition-transform transform active:scale-95"
            aria-label="Melodalis Home"
          >
            <div className="h-14 overflow-hidden flex items-center">
              <img 
                src="/images/logo-wide.jpg" 
                alt="Melodalis Logo" 
                className="h-14 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1.5 lg:gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`px-4 py-2 rounded-full font-bold text-sm lg:text-base flex items-center gap-2 transition-all cursor-pointer ${
                    isActive 
                      ? 'bg-gradient-to-r from-pink-400 to-rose-400 text-white shadow-md shadow-pink-200 scale-105' 
                      : 'text-purple-700/80 hover:text-pink-600 hover:bg-pink-50'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'animate-bounce' : ''}`} />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Right side controls */}
          <div className="flex items-center gap-3">
            {/* YouTube external button */}
            <a
              href="https://www.youtube.com/@Melodalis-music"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => soundEngine.playPopSfx()}
              className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-red-50 text-red-600 border border-red-200 text-xs font-bold hover:bg-red-500 hover:text-white transition-all shadow-xs"
            >
              <YoutubeIcon className="w-4 h-4 fill-current" />
              <span>@Melodalis-music</span>
            </a>

            {/* Kawaii Sound FX Toggle */}
            <button
              onClick={toggleSound}
              title={isMuted ? 'Turn on sound effects' : 'Mute sound effects'}
              className={`p-2.5 rounded-full border transition-all cursor-pointer ${
                isMuted
                  ? 'bg-gray-100 text-gray-400 border-gray-200'
                  : 'bg-pink-100 text-pink-600 border-pink-300 hover:bg-pink-200 animate-pulse-glow'
              }`}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>

            {/* Mobile menu hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 md:hidden text-purple-700 hover:text-pink-600 rounded-lg"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white/95 border-b border-pink-200 px-4 pt-2 pb-6 space-y-2 animate-fadeIn shadow-lg">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`w-full px-4 py-3 rounded-2xl font-bold flex items-center gap-3 text-left transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-pink-400 to-rose-400 text-white shadow-sm'
                    : 'text-purple-800 hover:bg-pink-50'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
          <div className="pt-2">
            <a
              href="https://www.youtube.com/@Melodalis-music"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-red-500 text-white font-bold shadow-sm"
            >
              <YoutubeIcon className="w-5 h-5" />
              Subscribe on YouTube
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
